import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const mitigationPsychrometricsConfigurationValueSchema = z.strictObject({
    pageEnabled: z.boolean(),
    atmosphericPressurePascals: z.number().int().min(50_000).max(120_000),
    targetGppMillis: z.number().int().min(0).max(500_000),
    lightMaxDifferenceMillis: z.number().int().min(0).max(500_000),
    moderateMaxDifferenceMillis: z.number().int().min(0).max(500_000),
}).superRefine((value, context) => {
    if (value.moderateMaxDifferenceMillis < value.lightMaxDifferenceMillis) {
        context.addIssue({
            code: 'custom',
            path: ['moderateMaxDifferenceMillis'],
            message: 'The moderate threshold must be at least the light threshold.',
        });
    }
});
export const mitigationPsychrometricsConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'mitigation.psychrometrics.configuration',
        moduleId: 'mitigation',
        title: 'Mitigation psychrometrics configuration',
        helpText: 'Controls tenant pressure, target GPP, and recommendation bands. Targets are tenant operating guidance, not a universal industry standard.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageEnabled: true,
            atmosphericPressurePascals: 101_325,
            targetGppMillis: 50_000,
            lightMaxDifferenceMillis: 20_000,
            moderateMaxDifferenceMillis: 40_000,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: mitigationPsychrometricsConfigurationValueSchema,
});
export const mitigationPsychrometricsDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    decimalPlaces: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    showAdvancedValues: z.boolean(),
});
export const mitigationPsychrometricsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.psychrometrics.display',
        moduleId: 'mitigation',
        title: 'Mitigation psychrometrics display',
        helpText: 'Controls psychrometrics density, card size, precision, and advanced result visibility.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            density: 'comfortable',
            cardSize: 'standard',
            decimalPlaces: 1,
            showAdvancedValues: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationPsychrometricsDisplayValueSchema,
});
const label = z.string().trim().min(1).max(160);
const message = z.string().trim().min(1).max(600);
export const mitigationPsychrometricsCopyValueSchema = z.strictObject({
    navigationLabel: label,
    title: label,
    description: message,
    guidanceNotice: message,
    temperatureLabel: label,
    relativeHumidityLabel: label,
    atmosphericPressureLabel: label,
    targetGppLabel: label,
    calculateAction: label,
    resultTitle: label,
    temperatureCelsiusLabel: label,
    dewPointLabel: label,
    grainsPerPoundLabel: label,
    grainDifferenceLabel: label,
    specificHumidityLabel: label,
    vaporPressureLabel: label,
    recommendationLabel: label,
    recommendationBands: z.record(z.enum(['at-or-below-target', 'light', 'moderate', 'heavy']), message),
    fahrenheitUnitLabel: label,
    percentUnitLabel: label,
    pascalUnitLabel: label,
    gppUnitLabel: label,
    gramsPerKilogramUnitLabel: label,
});
export const mitigationPsychrometricsCopySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.copy.psychrometrics',
        moduleId: 'mitigation',
        title: 'Mitigation psychrometrics copy',
        helpText: 'Controls tenant-editable psychrometric labels and operating guidance.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Psychrometrics',
            title: 'Psychrometric calculator',
            description: 'Calculate dew point, grains per pound, and humidity relationships from field conditions.',
            guidanceNotice: 'The target and recommendation bands are tenant-configured operating guidance. Confirm equipment decisions against the project conditions and your current procedures.',
            temperatureLabel: 'Dry-bulb temperature',
            relativeHumidityLabel: 'Relative humidity',
            atmosphericPressureLabel: 'Atmospheric pressure',
            targetGppLabel: 'Tenant target GPP',
            calculateAction: 'Calculate conditions',
            resultTitle: 'Calculated conditions',
            temperatureCelsiusLabel: 'Temperature Celsius',
            dewPointLabel: 'Dew point',
            grainsPerPoundLabel: 'Grains per pound',
            grainDifferenceLabel: 'Difference from tenant target',
            specificHumidityLabel: 'Specific humidity',
            vaporPressureLabel: 'Actual vapor pressure',
            recommendationLabel: 'Operating guidance band',
            recommendationBands: {
                'at-or-below-target': 'At or below the tenant target. Continue monitoring actual material and air conditions.',
                light: 'Light difference from the tenant target. Review field trends before adjusting the drying plan.',
                moderate: 'Moderate difference from the tenant target. Reassess containment, air movement, and dehumidification performance.',
                heavy: 'Large difference from the tenant target. Promptly reassess the drying environment and documented equipment plan.',
            },
            fahrenheitUnitLabel: '°F',
            percentUnitLabel: '%',
            pascalUnitLabel: 'Pa',
            gppUnitLabel: 'GPP',
            gramsPerKilogramUnitLabel: 'g/kg',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationPsychrometricsCopyValueSchema,
});
export const mitigationPsychrometricsSettingRegistrations = [
    mitigationPsychrometricsConfigurationSetting,
    mitigationPsychrometricsDisplaySetting,
    mitigationPsychrometricsCopySetting,
];
//# sourceMappingURL=mitigation-psychrometrics-setting-definitions.js.map